import { OmniaConfig } from "../../config"
import { RegionId } from "../../generated/enums"
import { param } from "../../generated/labels"
import { omniaResolve } from "../../promises"
import { IOmniaShell } from "../../shell"
import { checkPassword, getPasswordError, msgPasswordRule } from "../../user/password/password"
import { defineDirective } from "../../utils/angular"
import { getDistinctValues, groupBy, isArrayEmpty, selectMany } from "../../utils/array"
import { isUndefinedOrNull } from "../../utils/utils"

interface Town {
    id: number
    region: RegionId
    name: string
    postCodes: string[]
}

interface SignupServiceData {

    email: string
    firstName: string
    lastName: string

    holdingInvites: {
        holdingId: number
        holdingName: string
        issuerName: string
        issuerFirstName: string
        issuerLastName: string
        issuerEmail: string
    }[]

    towns: Town[]

    tcs: {
        version: string,
        text: string
    }
}

enum Steps {
    TCS = 1,
    Contact = 2,
    Holding = 3,
    Password = 4
}

/** @internal */
export class SignupCtrl {

    static create(): ng.IDirective {

        return defineDirective(SignupCtrl, "shared/main/signup/signup.html")

    }

    form: ng.IFormController

    steps = [
        { step: Steps.TCS, text: "Terms & conditions", icon: "fas fa-file-signature" },
        { step: Steps.Contact, text: "Contact", icon: "far fa-address-card" },
        { step: Steps.Holding, text: "Holding", icon: "fas fa-home" },
        { step: Steps.Password, text: "Password", icon: "fas fa-lock" },
    ]

    step = Steps.TCS

    username = ""
    firstName = ""
    lastName = ""

    passwordRuleLabel = msgPasswordRule
    password: string
    password2: string

    phoneNumber: string
    postCode: string
    address: string

    holdingName: string
    holdingExists = false

    accountNumber = ""
    agronomist = ""

    regionId: number
    regionAuto = false

    postCodeIndex: Map<string, Town[]>
    towns: Town[] = []
    allTowns: Town[] = []

    town: Town
    postTownAuto = false
    postTownDropdown = false

    regionTowns: Town[]

    addressLookupCache: { [key: string]: any } = {}

    tcs = {
        text: "",
        html: "",
        version: ""
    }

    acceptTcs = false
    acceptContact = undefined as boolean

    loadingAddr = false

    showValidationErrors = false

    holdingInvites: SignupServiceData["holdingInvites"]
    createNewHoldingIfInvited = false

    static $inject = ["OmniaShell", "omniaConfig"]

    constructor(
        private readonly shell: IOmniaShell,
        private readonly config: OmniaConfig,
        private readonly session = shell.getSession(),
        private readonly ohttp = session.http,
        public regions = getRegions(config.regions)
    ) {
    }

    $onInit() {

        this.ohttp.get("/api/signup/v1/data").then(r => {

            const d: SignupServiceData = r.data

            this.username = d.email
            this.firstName = d.firstName
            this.lastName = d.lastName

            this.holdingInvites = isArrayEmpty(d.holdingInvites)
                ? undefined
                : d.holdingInvites.map(d => ({
                    ...d,
                    issuerName: [d.issuerFirstName, d.issuerLastName].filter(t => !!t).join(" ")
                }))

            this.towns = this.allTowns = d.towns || []
            this.postCodeIndex = getPostCodeIndex(this.allTowns)

            this.tcs = formatTcs(d.tcs)

            console.log("init", { d, inv: this.holdingInvites })

        })

    }

    filterTowns() {
        this.regionTowns = this.towns.filter(t => (!t.region) || (t.region == this.regionId))
        this.town = (this.town && this.regionTowns.some(t => t.id == this.town.id)) ? this.town : undefined
        this.town = (!this.town) && (this.regionTowns.length == 1)
            ? this.regionTowns[0]
            : this.town
    }

    onPostCodeUpdated() {

        this.regions = getRegions(this.config.regions)
        this.regionAuto = false
        this.regionId = undefined

        this.postTownAuto = false
        this.town = undefined
        this.towns = this.allTowns
        this.postTownDropdown = false

        this.filterTowns()

        const prefix = (/^\s*([A-Z]{1,2})/i).exec(this.postCode)?.[1]

        if (!prefix) {
            return
        }

        const towns = this.postCodeIndex.get(prefix.toLocaleUpperCase())

        if (!towns) {
            return
        }

        const regions = getDistinctValues(towns, t => t.region)

        this.regions = this.regions.filter(r => regions.indexOf(r.value) >= 0)
        this.towns = towns

        if (regions.length == 1) {
            this.regionId = regions[0]
            this.regionAuto = true
        }

        if (towns.length == 1) {
            this.town = towns[0]
            this.postTownAuto = true
        }

        this.postTownDropdown = true

        this.filterTowns()

    }

    lookupAddress(query: string) {

        query = encodeURIComponent(query)

        const cached = this.addressLookupCache[query]

        if (cached !== undefined) {
            return omniaResolve(cached)
        }

        this.loadingAddr = true

        return this.ohttp.get(`/api/signup/v1/addresses/lookup/${query}`, false).then(r => {

            this.loadingAddr = false
            return this.addressLookupCache[query] = (r.data || null)

        })

    }

    onAddressSelected(item: { udprn: string }) {

        if (item.udprn) {

            return this.ohttp.get(`/api/signup/v1/addresses/get/${item.udprn}`).then(r => {

                const address = r.data as {
                    line_1: string,
                    line_2: string,
                    line_3: string,
                    post_town: string,
                    postcode: string
                }

                if (r.data) {

                    const result = [
                        address.line_1,
                        address.line_2,
                        address.line_3,
                        address.post_town,
                        address.postcode,
                    ].filter(e => !!e).join("\n")

                    this.address = result
                    this.postCode = address.postcode
                    this.onPostCodeUpdated()

                }

            })

        }

    }

    checkPassword() {
        const { password, password2 } = this
        const e = checkPassword(password, password2)
        return getPasswordError(e)
    }

    validate() {

        if (!this.acceptTcs) return false

        if (this.form.$invalid) return false

        if (this.step == Steps.Password) {
            const pwderr = this.checkPassword()
            if (pwderr) return false
        }

        return true

    }

    canCommit() {

        const { acceptTcs, acceptContact, showValidationErrors } = this

        if (!acceptTcs) return false
        if (isUndefinedOrNull(acceptContact)) return false
        if (!showValidationErrors) return true

        return this.validate()

    }

    goToPrevious() {
        this.step = Math.max(1, this.step - 1)
    }

    commit() {

        if (!this.validate()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        if ((this.step == Steps.TCS) && (!this.acceptContact)) {

            confirmContactChoice(this.shell).then(close => {

                if (close) {
                    const url = `/api/signup/v1/cancel`
                    return this.ohttp.post(url).finally(() => this.session.logout())
                }

            })

            return

        }


        if (this.step == Steps.Password) {
            return this.confirm()
        }

        this.step = this.step + 1

    }

    private confirm() {

        const {
            firstName,
            lastName,
            postCode,
            address,
            phoneNumber,
            regionId,
            town,
            holdingName,
            agronomist,
            accountNumber,
            holdingExists,
            password,
            createNewHoldingIfInvited,
            holdingInvites
        } = this

        const invited = !isArrayEmpty(holdingInvites)

        const data = {
            firstName,
            lastName,
            postCode,
            address,
            phoneNumber,
            regionId,
            townId: town ? town.id : undefined,
            holdingName,
            agronomist,
            accountNumber,
            holdingExists: holdingExists || (invited && (!createNewHoldingIfInvited)),
            password,
            termsOfUseVersion: this.tcs.version
        }

        return this.ohttp.post("/api/signup/v1/setup", data).then(r => {

            const { error } = r.data || {}

            if (!error) {
                return this.session.login({ username: this.session.currentUser.Email, password: this.password })
            }
        })


    }

}

function formatTcs(tcs: SignupServiceData["tcs"]) {

    return {
        text: tcs.text,
        html: tcs.text,
        version: tcs.version
    }
}

function getPostCodeIndex(towns: SignupServiceData["towns"]) {

    towns = towns.filter(t => !isArrayEmpty(t.postCodes))

    const a = selectMany(towns.map(town => town.postCodes.map(postCode => ({ town, postCode }))))
    const b = a.filter(i => !!i.postCode)
    const c = groupBy(b, i => i.postCode.toLocaleUpperCase()).map(i => <[string, Town[]]>[i.key, i.values.map(t => t.town)])

    return new Map(c)

}

function confirmContactChoice(shell: IOmniaShell) {

    const title = "Omnia signup"
    const content = "<p>We are sorry, but without your consent, we are not able to finish setting up your account.</p>"

    return shell.showMessageBox({
        title,
        content,
        buttons: [
            { text: "Go back", value: false, cssClass: "btn-link" },
            { text: "Cancel my account", value: true, cssClass: "btn-warning" },
        ]
    })

}

function getRegions(regions: RegionId[]) {
    const set = new Set(regions)
    return param.RegionId().filter(r => set.has(r.value))
}
